import { Button, Flex, Result } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { BodyContainer } from '../../../../elements';
import {
  ShipmentDetailHook,
  shipmentDetailsExtractor,
} from '../../../../hooks/shipments';
import { useAppHeaderLead } from '../../../../hooks/useAppHeaderLead';
import { useSecondaryHeader } from '../../../../hooks/useSecondaryHeader';
import { InboundShipmentAppHeaderLead } from '../../../../page-components/shipments/inbound/detail/InboundShipmentAppHeaderLead';
import { InboundShipmentDetailHeader } from '../../../../page-components/shipments/inbound/detail/InboundShipmentDetailHeader';
import { InboundShipmentDetailLayout } from '../../../../page-components/shipments/inbound/detail/InboundShipmentDetailLayout';
import {
  InboundShipmentDetailSkeleton,
  InboundShipmentSecondaryHeaderSkeleton,
} from '../../../../page-components/shipments/inbound/detail/InboundShipmentDetailSkeleton';
import {
  getEffectiveShipmentStatus,
  getInboundWorkflowStepIndex,
  INBOUND_SHIPMENTS_LIST,
  resolveInboundDetailDisplay,
} from '@utility/utils/shipment-display';

export function InboundShipmentDetailScreen() {
  const { id } = useParams();
  const { data, loading, refresh, error } = ShipmentDetailHook(id);
  const { summary, cbm } = shipmentDetailsExtractor(data);

  const display = data?._id ? resolveInboundDetailDisplay(data) : null;
  const isInitialLoad = Boolean(id) && (loading || (!data?._id && !error));
  const stepIndex = data?._id
    ? Math.max(0, getInboundWorkflowStepIndex(getEffectiveShipmentStatus(data)))
    : 0;

  useAppHeaderLead(
    () => (
      <InboundShipmentAppHeaderLead displayId={data?.displayId} loading={isInitialLoad} />
    ),
    [data?.displayId, isInitialLoad],
  );

  useSecondaryHeader(() => {
    if (isInitialLoad) {
      return <InboundShipmentSecondaryHeaderSkeleton />;
    }
    if (!data?._id) {
      return null;
    }
    return <InboundShipmentDetailHeader data={data} stepIndex={stepIndex} />;
  }, [isInitialLoad, data, stepIndex]);

  if (isInitialLoad) {
    return (
      <BodyContainer className="no-header">
        <InboundShipmentDetailSkeleton />
      </BodyContainer>
    );
  }

  if (!loading && !data?._id) {
    return (
      <BodyContainer className="no-header">
        <Flex justify="center" style={{ paddingTop: 48 }}>
          <Result
            status="404"
            title="Shipment not found"
            subTitle={
              error
                ? 'Unable to load this inbound shipment.'
                : 'This inbound shipment does not exist or was removed.'
            }
            extra={
              <Link to={INBOUND_SHIPMENTS_LIST}>
                <Button type="primary">Back to Shipments</Button>
              </Link>
            }
          />
        </Flex>
      </BodyContainer>
    );
  }

  return (
    <BodyContainer className="no-header">
      <InboundShipmentDetailLayout
        data={data}
        display={display}
        loading={loading}
        summary={summary}
        cbm={cbm}
        onRefresh={refresh}
      />
    </BodyContainer>
  );
}
